import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { 
  Camera, 
  Wand2, 
  Printer, 
  Wifi, 
  WifiOff, 
  Settings,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import CharacterSelector from './CharacterSelector';
import CameraCapture from './CameraCapture';
import ResultDisplay from './ResultDisplay';
import PrintStatusPanel from './PrintStatusPanel';
import printService from '../../services/printService';

export default function TabletPhotobooth() {
  const { t, language, isRTL } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [printSettings, setPrintSettings] = useState({
    size: '4x6',
    quality: 'standard',
    copies: 1,
    autoPrint: true
  });
  const [showSettings, setShowSettings] = useState(false);
  const [printQueue, setPrintQueue] = useState([]);

  useEffect(() => {
    initializePrintService();
    return () => {
      printService.disconnect();
    };
  }, []);

  const initializePrintService = async () => {
    try {
      printService.on('connected', () => {
        setConnectionStatus('connected');
        printService.getPrinters();
      });

      printService.on('disconnected', () => {
        setConnectionStatus('disconnected');
      });

      printService.on('printers', (printerList) => {
        setPrinters(printerList);
        if (printerList.length > 0 && !selectedPrinter) {
          setSelectedPrinter(printerList[0].name);
        }
      });

      printService.on('printStatus', (status) => {
        // Update print queue status
        setPrintQueue(prev => prev.map(job => 
          job.id === status.jobId ? { ...job, status: status.status } : job
        ));
      });

      printService.on('jobCompleted', (job) => {
        setPrintQueue(prev => prev.filter(j => j.id !== job.id));
      });

      printService.on('error', (error) => {
        console.error('Print service error:', error);
        setError(language === 'he' ? 'שגיאת הדפסה' : 'Print error');
      });

      await printService.connect();
    } catch (error) {
      console.error('Failed to connect to print service:', error);
      setConnectionStatus('error');
    }
  };

  const handleCharacterSelect = (character) => {
    setSelectedCharacter(character);
    setStep(2);
  };

  const handleCapture = (capture) => {
    if (capture && typeof capture === 'object') {
      setCapturedImage(capture.previewUrl || null);
      setCapturedFile(capture.file || null);
    } else {
      setCapturedImage(capture);
      setCapturedFile(null);
    }
  };

  const handleGenerate = async () => {
    if (!selectedCharacter || !capturedImage || !capturedFile) return;

    setError(null);
    setIsGenerating(true);
    setStep(3);

    try {
      // Here you would call your existing image generation API
      // For now, simulating with the captured image
      const mockGeneratedImage = capturedImage;
      setGeneratedImage(mockGeneratedImage);

      // Auto-print if enabled
      if (printSettings.autoPrint && connectionStatus === 'connected') {
        handlePrint(mockGeneratedImage);
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(language === 'he' ? 'שגיאה ביצירת התמונה' : 'Error generating image');
      setStep(2);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = (imageToPrint = generatedImage) => {
    if (!imageToPrint || !selectedPrinter) return;

    const jobId = printService.printImage(imageToPrint, {
      printerName: selectedPrinter,
      ...printSettings
    });

    setPrintQueue(prev => [...prev, {
      id: jobId,
      image: imageToPrint,
      status: 'queued',
      timestamp: new Date()
    }]);
  };

  const handleTryAnother = () => {
    setSelectedCharacter(null);
    setCapturedImage(null);
    setCapturedFile(null);
    setGeneratedImage(null);
    setStep(1);
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <Wifi className="w-4 h-4 text-green-400" />;
      case 'disconnected':
        return <WifiOff className="w-4 h-4 text-red-400" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />;
    }
  };

  const getConnectionText = () => {
    switch (connectionStatus) {
      case 'connected':
        return language === 'he' ? 'מחובר' : 'Connected';
      case 'disconnected':
        return language === 'he' ? 'מנותק' : 'Disconnected';
      case 'error':
        return language === 'he' ? 'שגיאת חיבור' : 'Connection Error';
      default:
        return language === 'he' ? 'מתחבר...' : 'Connecting...';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          {step > 1 && step < 3 && (
            <Button
              onClick={() => setStep(step - 1)}
              variant="ghost"
              size="icon"
              className="text-white hover:text-white/80"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
          )}
          <h1 className="text-2xl font-bold text-white">
            {language === 'he' ? 'עמדת צילום' : 'Photo Booth'}
          </h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Connection Status */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/20">
            {getConnectionIcon()}
            <span className="text-sm text-white">{getConnectionText()}</span>
          </div>

          {/* Settings Button */}
          <Button
            onClick={() => setShowSettings(!showSettings)}
            variant="ghost"
            size="icon"
            className="text-white hover:text-white/80"
          >
            <Settings className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-b border-white/10 bg-black/20 p-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Printer Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {language === 'he' ? 'מדפסת' : 'Printer'}
                </label>
                <select
                  value={selectedPrinter || ''}
                  onChange={(e) => setSelectedPrinter(e.target.value)}
                  className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white"
                  disabled={connectionStatus !== 'connected'}
                >
                  {printers.map((printer) => (
                    <option key={printer.name} value={printer.name}>
                      {printer.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Print Size */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {language === 'he' ? 'גודל הדפסה' : 'Print Size'}
                </label>
                <select
                  value={printSettings.size}
                  onChange={(e) => setPrintSettings(prev => ({ ...prev, size: e.target.value }))}
                  className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white"
                >
                  <option value="4x6">4x6</option>
                  <option value="5x7">5x7</option>
                  <option value="8x10">8x10</option>
                </select>
              </div>

              {/* Auto Print Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white">
                  {language === 'he' ? 'הדפסה אוטומטית' : 'Auto Print'}
                </label>
                <button
                  onClick={() => setPrintSettings(prev => ({ ...prev, autoPrint: !prev.autoPrint }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    printSettings.autoPrint ? 'bg-violet-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      printSettings.autoPrint ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Connection Error Alert */}
      {connectionStatus === 'error' && (
        <Alert className="m-4 bg-red-500/10 border-red-500/30">
          <AlertCircle className="h-4 w-4 text-red-400" />
          <AlertDescription className="text-red-400">
            {language === 'he' 
              ? 'לא ניתן להתחבר לשרת ההדפסה. יש לוודא שהתוכנה פועלת במחשב.'
              : 'Cannot connect to print server. Please ensure the desktop application is running.'
            }
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <div className="p-4">
        <AnimatePresence mode="wait">
          {/* Step 1: Character Selection */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-white mb-6">
                  {language === 'he' ? 'בחר דמות' : 'Select Character'}
                </h2>
                <CharacterSelector
                  selectedCharacter={selectedCharacter}
                  onSelect={handleCharacterSelect}
                  characters={[]} // Pass your characters here
                />
              </div>
            </motion.div>
          )}

          {/* Step 2: Camera */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Character Preview */}
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {language === 'he' ? 'הדמות שנבחרה' : 'Selected Character'}
                  </h3>
                  {selectedCharacter && (
                    <div className="relative rounded-xl overflow-hidden">
                      <img
                        src={selectedCharacter.image_url}
                        alt={selectedCharacter.name}
                        className="w-full aspect-[3/4] object-cover"
                      />
                    </div>
                  )}
                  <Button
                    onClick={() => setStep(1)}
                    className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white"
                  >
                    {language === 'he' ? 'שנה דמות' : 'Change Character'}
                  </Button>
                </div>

                {/* Camera */}
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {language === 'he' ? 'צלם תמונה' : 'Take Photo'}
                  </h3>
                  <CameraCapture
                    onCapture={handleCapture}
                    capturedImage={capturedImage}
                    onClear={() => setCapturedImage(null)}
                  />
                  
                  {capturedImage && (
                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="w-full mt-4 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14 text-lg"
                    >
                      <Wand2 className="w-5 h-5 mr-2" />
                      {language === 'he' ? 'צור קסם!' : 'Create Magic!'}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Result */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="glass-card rounded-2xl p-6">
                <ResultDisplay
                  originalImage={capturedImage}
                  generatedImage={generatedImage}
                  characterName={selectedCharacter?.name}
                  onTryAnother={handleTryAnother}
                  isLoading={isGenerating}
                />
                
                {/* Print Button */}
                {!printSettings.autoPrint && generatedImage && (
                  <div className="mt-6 flex justify-center">
                    <Button
                      onClick={() => handlePrint()}
                      disabled={connectionStatus !== 'connected'}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8"
                    >
                      <Printer className="w-5 h-5 mr-2" />
                      {language === 'he' ? 'הדפס' : 'Print'}
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Print Status Panel */}
      {printQueue.length > 0 && (
        <PrintStatusPanel
          queue={printQueue}
          connectionStatus={connectionStatus}
        />
      )}
    </div>
  );
}
